import { useEffect, useMemo, useRef, useState } from "react";
import { signal as _signal, type ReactiveSignal } from "@decocms/blocks/sdk/signal";
export { signal, type ReactiveSignal } from "@decocms/blocks/sdk/signal";

/**
 * Module-level computed signal. Re-evaluates on access via getter.
 * Note: not reactive in React render — use useStore() for component reactivity.
 */
export function computed<T>(fn: () => T): ReactiveSignal<T> {
  const s = _signal<T>(fn());
  return {
    ...s,
    get value() {
      const v = fn();
      return v;
    },
    peek() {
      return fn();
    },
  } as ReactiveSignal<T>;
}

/** Drop-in replacement for preact's useSignalEffect — runs the callback after render. */
export function useSignalEffect(fn: () => void | (() => void)): void {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => fn(), []);
}

/**
 * Legacy module-level side effect. The original @preact/signals `effect`
 * tracked signal reads and re-ran on change; our shim runs the callback once
 * at module load time. We schedule it to fire only in the browser so SSR /
 * worker analysis won't blow up if the callback uses React hooks, `self`,
 * `window`, or anything else that doesn't exist on the server.
 */
export function effect(fn: () => void | (() => void)): () => void {
  if (typeof window === "undefined") return () => {};
  let cleanup: void | (() => void);
  // Defer to the next microtask so module evaluation finishes first.
  queueMicrotask(() => {
    cleanup = fn();
  });
  return () => {
    if (typeof cleanup === "function") cleanup();
  };
}

/**
 * Component-level signal compat: wraps useState so reads of `.value` return the
 * latest state and writes trigger re-renders. Migration target = useState; this
 * shim lets the bulk of components compile without rewriting each call site.
 */
export interface ComponentSignal<T> {
  value: T;
  peek(): T;
}

export function useSignal<T>(initial: T): ComponentSignal<T> {
  const [state, setState] = useState<T>(initial);
  const stateRef = useRef(state);
  stateRef.current = state;
  return useMemo<ComponentSignal<T>>(
    () => ({
      get value() {
        return stateRef.current;
      },
      set value(v: T) {
        stateRef.current = v;
        setState(v);
      },
      peek() {
        return stateRef.current;
      },
    }),
    [],
  );
}

export function useComputed<T>(fn: () => T): ComponentSignal<T> {
  const v = fn();
  const ref = useRef(v);
  ref.current = v;
  return useMemo<ComponentSignal<T>>(
    () => ({
      get value() {
        return ref.current;
      },
      peek() {
        return ref.current;
      },
    }),
    [],
  );
}
